import { IntegrityMonitorPolicyRuleSetsSectionView } from '../view/IntegrityMonitorPolicyRuleSetsSectionView';
import { templateIntegrityMonitorPolicyRuleSetsSection } from '../view/template';
import { IntegrityMonitorPolicyRuleSetsSectionRepository } from '../repository/IntegrityMonitorPolicyRuleSetsSectionRepository';
import { BaseWebComponentController, IListenerConfig } from '../../../../../framework/BaseWebComponentController';
import { IntegrityMonitorPolicyRuleSetsSectionWebComponent } from '..';
import { IntegrityMonitorPolicyRuleSetsSectionWebComponentSchema, PolicyRuleSetsSectionEvents } from '../types';
import { EnumListEvents } from '../../../../../ui/gzEnumList/types';

export class IntegrityMonitorPolicyRuleSetsSectionController extends BaseWebComponentController {
  public component: IntegrityMonitorPolicyRuleSetsSectionWebComponent;
  public dataRepository: IntegrityMonitorPolicyRuleSetsSectionRepository;
  public view: IntegrityMonitorPolicyRuleSetsSectionView;
  public viewConstructor: typeof IntegrityMonitorPolicyRuleSetsSectionView;
  public dataRepositoryConstructor: typeof IntegrityMonitorPolicyRuleSetsSectionRepository;

  constructor(component: IntegrityMonitorPolicyRuleSetsSectionWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: IntegrityMonitorPolicyRuleSetsSectionView,
      viewTemplate: templateIntegrityMonitorPolicyRuleSetsSection,
      dataRepositoryConstructor: IntegrityMonitorPolicyRuleSetsSectionRepository,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$ruleSetsSelector,
        event: EnumListEvents.SELECTION_CHANGE,
        callback: this.onRuleSetsSelectionChange,
      },
      {
        element: this.view.dom.$grid,
        event: PolicyRuleSetsSectionEvents.REMOVE_RULE_SET,
        callback: this.onRemoveRuleSet,
      },
    ];
  }

  async getDataForView(): Promise<IntegrityMonitorPolicyRuleSetsSectionWebComponentSchema> {
    if (!this.component.selectedRuleSetIds) {
      return {
        selectedRuleSets: [],
        companyRuleSets: [],
        isDisabled: () => true,
      };
    }

    this.component.companyRuleSets = await this.dataRepository.getCompanyRuleSetsForPolicy();
    const selectedRuleSets = await this.dataRepository.getRuleSetsForPolicy(this.component.selectedRuleSetIds);

    return {
      selectedRuleSets,
      companyRuleSets: this.component.companyRuleSets,
      isDisabled: () => this.component.disabled,
    };
  }

  private onRuleSetsSelectionChange(event) {
    const selectedRuleSetIds = event.detail.selectedValues;
    const oldSelectedRuleSetIds = this.component.selectedRuleSetIds;
    const newRuleSetIds = selectedRuleSetIds.filter(ruleSetId => !oldSelectedRuleSetIds.includes(ruleSetId));
    const newRuleSets = this.component.companyRuleSets.filter(ruleSet => newRuleSetIds.includes(ruleSet._id));

    // Update the selectedRuleSetIds property
    this.component.selectedRuleSetIds.push(...newRuleSetIds);

    // Disable all of the options that have been selected
    this.view.dom.$ruleSetsSelector.filterOptions = this.view.dom.$ruleSetsSelector.filterOptions.map(option => {
      return {
        ...option,
        disabled: option.selected,
      };
    });

    // Add the newly selected rule sets to the grid
    this.view.dom.$grid.dataProvider.ruleSets.push(...newRuleSets);
    this.view.dom.$grid.controller.render();

    // Notify the parent component
    this.dispatchRuleSetsSelectionChangeEvent();
  }

  private onRemoveRuleSet(event) {
    const removedRuleSet = event.detail.ruleSet;

    // Update the selectedRuleSetIds property
    this.component.selectedRuleSetIds = this.component.selectedRuleSetIds.filter(id => id !== removedRuleSet._id);

    // Unselect and Enable the rule set's option from the dropdown
    this.view.dom.$ruleSetsSelector.filterOptions = this.view.dom.$ruleSetsSelector.filterOptions.map(option => {
      const isSelectedAndDisabled = this.component.selectedRuleSetIds.includes(option.value);
      return {
        ...option,
        selected: isSelectedAndDisabled,
        disabled: isSelectedAndDisabled,
      };
    });

    // Remove the rule set item from the grid
    const removedRuleSetIndex = this.view.dom.$grid.dataProvider.ruleSets.findIndex(
      ruleSet => ruleSet._id === removedRuleSet._id
    );
    this.view.dom.$grid.dataProvider.ruleSets.splice(removedRuleSetIndex, 1);
    this.view.dom.$grid.controller.render();

    // Notify the parent component
    this.dispatchRuleSetsSelectionChangeEvent();
  }

  private dispatchRuleSetsSelectionChangeEvent() {
    this.component.dispatchEvent(
      new CustomEvent(PolicyRuleSetsSectionEvents.RULE_SETS_SELECTION_CHANGE, {
        detail: {
          selectedRuleSetIds: this.component.selectedRuleSetIds,
        },
        bubbles: true,
        cancelable: true,
      })
    );
  }
}
